<template>
  <v-card elevation="0">
    <v-overlay :value="isResolvingWorker" absolute>
      <div class="d-flex flex-column align-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="42"
          width="4"
        />
        <span class="mt-3">Validando trabajador y contrato activo...</span>
      </div>
    </v-overlay>
    <s-toolbar v-if="visibleTitle" label="Datos Persona" />
    <v-card-text>
      <!-- Sección 1: Identificación del trabajador -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="3" lg="4">
          <s-toolbar-person
            :defaultDocument="item.PrsDocumentNumber"
            @returnPerson="returnPerson($event, item)"
            v-model="item.PrsDocumentNumber"
            noCamera
            noEdit
            noSearch
            :showAddOrEdit="false"
            :readonly="true"
            :showCounter="true"
          />
        </v-col>
        <v-col class="s-col-form" cols="8" sm="8" md="8" lg="8">
          <s-text
            ref="textNtpName"
            label="Nombres y Apellidos Completos"
            v-model="item.NtpFullName"
            readonly
          />
        </v-col>
      </v-row>
      <!-- Sección 2: Origen / Requerimiento -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            ref="payrollRef"
            :def="1060"
            label="Planilla"
            v-model="item.TypePayroll"
            :add-custom="$fun.isAdmin()"
            :disabled="isPrimaryFieldDisabled"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Planilla',
                required: true,
                cols: 9,
              },
              {
                key: 'DedHelper',
                label: 'Código Sunat',
                required: true,
                cols: 3,
              },
            ]"
          />
        </v-col>
        <v-col
          v-if="hasSourceRequirements"
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
        >
          <s-select
            label="Requerimiento de origen"
            :items="sourceRequirementItems"
            item-text="text"
            item-value="value"
            v-model="selectedSourceRequirementKey"
            :disabled="isFieldsDisabled"
            clearable
          ></s-select>
        </v-col>
        <v-col v-if="sourceLabel" class="s-col-form" sm="4" md="4" lg="4">
          <s-text label="Origen" :value="sourceLabel" readonly />
        </v-col>
      </v-row>

      <!-- Helper de Vacantes del Requerimiento -->
      <v-row v-if="item.ReqID" class="px-3">
        <v-col cols="12" class="pt-0 pb-2">
          <v-alert
            type="info"
            outlined
            dense
            prominent
            icon="mdi-briefcase-account"
            class="mb-0"
          >
            <div class="d-flex flex-wrap align-center justify-space-between">
              <span class="font-weight-medium mr-4">
                Estado de Cobertura del Requerimiento #{{ item.ReqID }}:
              </span>
              <div class="d-flex gap-4 align-center">
                <v-chip color="primary" outlined class="mr-2">
                  <v-icon left small>mdi-numeric-1-box</v-icon>
                  Vacantes: {{ vacancyStatus.TotalVacancies }}
                </v-chip>
                <v-chip color="success" outlined class="mr-2">
                  <v-icon left small>mdi-check-circle</v-icon>
                  Cubiertas: {{ vacancyStatus.CoveredVacancies }}
                </v-chip>
                <v-chip color="warning" outlined>
                  <v-icon left small>mdi-file-document</v-icon>
                  Contratos Activos: {{ vacancyStatus.ActiveContracts }}
                </v-chip>
              </div>
            </div>

            <!-- Desglose por Turno y Género -->
            <div v-if="hasProjections" class="mt-3 pt-3 border-t-dotted">
              <div class="text-subtitle-2 mb-2 font-weight-bold">Cruce de Proyección Semanal y Contratos Activos:</div>
              <v-simple-table dense class="transparent-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left font-weight-bold">Turno</th>
                      <th class="text-center font-weight-bold">Proyectado</th>
                      <th class="text-center font-weight-bold">Contratados</th>
                      <th class="text-center font-weight-bold">Meta Hombres</th>
                      <th class="text-center font-weight-bold">Hombres Activos</th>
                      <th class="text-center font-weight-bold">Meta Mujeres</th>
                      <th class="text-center font-weight-bold">Mujeres Activas</th>
                      <th class="text-right font-weight-bold">Vacantes Libres</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in parsedProjections" :key="t.TypeTurn">
                      <td>{{ t.TurnName || 'Sin Turno' }}</td>
                      <td class="text-center">{{ t.TotalProyectado }}</td>
                      <td class="text-center">{{ t.TotalActivos }}</td>
                      <td class="text-center">{{ t.MaleProyectado }}</td>
                      <td class="text-center">{{ t.MaleActivos }}</td>
                      <td class="text-center">{{ t.FemaleProyectado }}</td>
                      <td class="text-center">{{ t.FemaleActivos }}</td>
                      <td class="text-right font-weight-bold" :class="(t.TotalProyectado - t.TotalActivos) > 0 ? 'success--text' : 'grey--text'">
                        {{ t.TotalProyectado - t.TotalActivos }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Sección 3: Ubicación organizacional -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-area
            ref="areaRef"
            :key="`area-${item.AreID || 0}-${item.AreName || ''}`"
            label="Área"
            :disabled="isPrimaryFieldDisabled"
            v-model="item.AreID"
            :text="item.AreName"
            full
          ></s-select-area>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-area
            ref="centerCostRef"
            :key="`cce-${selectedCenterCost || 0}-${item.CceName || ''}`"
            v-model="selectedCenterCost"
            :text="item.CceName"
            centercost
            :disabled="isPrimaryFieldDisabled"
            label="Centro de Costo"
            full
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-business-line
            ref="businessLineRef"
            :key="`bsl-${selectedCenterCost || 0}-${selectedBusinessLine || 0}`"
            v-model="selectedBusinessLine"
            :cceID="selectedCenterCost"
            full
            :allline="1"
            :disabled="isPrimaryFieldDisabled"
            label="Línea de Negocio"
          />
        </v-col>
      </v-row>

      <!-- Sección 4: Puesto y clasificación -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-position
            ref="positionRef"
            :key="`pst-${item.AreID || 0}-${item.PstID || 0}-${
              item.PstName || ''
            }`"
            v-model="item.PstID"
            :text="item.PstName"
            :AreID="item.AreID || 0"
            :disabled="isPrimaryFieldDisabled"
            label="Cargo"
            full
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <v-label>
            <b> Nivel de Criticidad </b>
          </v-label>
          <div class="mt-1">
            <v-chip
              :color="
                item.PstIsBascCritic == 1 || item.PstIsBascCritic == 0
                  ? 'white'
                  : item.PstIsBascCritic == 2
                  ? 'primary'
                  : item.PstIsBascCritic == 3
                  ? 'warning'
                  : 'error'
              "
              style="margin: 0px"
              >{{
                item.PstIsBascCritic == 0
                  ? "No Definido"
                  : item.PstIsBascCriticName
              }}
            </v-chip>
          </div>
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select
            label="Cuadrilla"
            :items="quadrille"
            autocomplete
            clearable
            item-text="GngName"
            item-value="GngID"
            v-model="item.GngID"
          ></s-select>
        </v-col>
      </v-row>

      <!-- Sección 5: Tipo de contrato y situación -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            ref="typeContractRef"
            :def="1069"
            label="Tipo de Contrato"
            v-model="item.TypeContract"
            :disabled="item._typeContractLocked"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Tipo de Contrato',
                required: true,
                cols: 9,
              },
              {
                key: 'DedHelper',
                label: 'Código Sunat',
                required: true,
                cols: 3,
              },
            ]"
            @input="onTypeContractChanged($event)"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1482"
            label="Categoria"
            v-model="item.typeCategory"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Categoría',
                required: true,
                cols: 12,
              },
            ]"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1039"
            :dgr="4"
            label="Tipo de Trabajador"
            v-model="item.TypeWorker"
            :text="item.TypeWorkerName"
            :addCustom="$fun.isAdmin()"
            :disabled="isFieldsDisabled"
            :customFields="[
              {
                key: 'DedDescription',
                label: 'Tipo de trabajador',
                required: true,
                cols: 8,
              },
              {
                key: 'DedHelper',
                label: 'Sunat',
                required: true,
                cols: 4,
              },
            ]"
          />
        </v-col>
      </v-row>

      <!-- Sección 5b: Establecimiento y Modalidad Formativa -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-establishment v-model="item.EstID" />
        </v-col>
        <v-col
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
          v-if="item.typeCategory == 4"
        >
          <s-select-definition
            :def="1716"
            label="Modalidad Formativa"
            v-model="item.TypeFormatLabor"
            :disabled="isFieldsDisabled"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Modalidad Formativa',
                required: true,
                cols: 9,
              },
              {
                key: 'DedHelper',
                label: 'Código Sunat',
                required: true,
                cols: 3,
              },
            ]"
          />
        </v-col>
      </v-row>

      <!-- Sección 6: Datos laborales complementarios -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1481"
            label="Régimen laboral"
            v-model="item.TypeLaborRegime"
            :text="item.LaborRegimeText"
            :addCustom="$fun.isAdmin()"
            :disabled="isFieldsDisabled"
            :customFields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Régimen',
                required: true,
                cols: 8,
              },
              {
                key: 'DedHelper',
                label: 'Sunat',
                required: true,
                cols: 4,
              },
            ]"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1077"
            label="Cat. Ocupacional"
            v-model="item.TypeOccupationalCategory"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Categoría',
                required: true,
                cols: 8,
              },
              {
                key: 'DedHelper',
                label: 'Sunat',
                required: true,
                cols: 4,
              },
            ]"
          />
        </v-col>
      </v-row>

      <!-- Sección 7: Datos personales del contrato -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1071"
            label="Estado civil"
            v-model="item.TypeCivilStatus"
            :add-custom="$fun.isAdmin()"
            :disabled="isFieldsDisabled"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Estado Civil',
                required: true,
                cols: 6,
              },
              {
                key: 'DedAbbreviation',
                label: 'Abreviatura',
                required: true,
                cols: 3,
              },
              {
                key: 'DedHelper',
                label: 'Sunat',
                required: true,
                cols: 3,
              },
            ]"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-text
            label="N° de hijos"
            number
            v-model="item.AgtChildrenQuantity"
            :min="0"
            :disabled="isFieldsDisabled"
          />
        </v-col>
      </v-row>
      <!-- Sección 8: Remuneración y condiciones -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-protected-admin-field
            ref="salaryProtectedField"
            label="Sueldo/Jornal"
            v-model="item.AgtSalary"
            :encrypted="true"
            :agtID="Number(item.AgtID || 0)"
            :pstID="Number(item.PstID || 0)"
            :canViewByDefault="$fun.isAdmin()"
            :alwaysMasked="true"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1278"
            label="Turno"
            v-model="item.TypeTurno"
            :add-custom="$fun.isAdmin()"
            :disabled="isFieldsDisabled"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Turno',
                required: true,
                cols: 12,
              },
            ]"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1045"
            label="Situación"
            v-model="item.TypeSituation"
            :add-custom="$fun.isAdmin()"
            :disabled="isFieldsDisabled"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Situación',
                required: true,
                cols: 12,
              },
            ]"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-campaign-se
            :key="`cmp-${item.CmpID || 0}-${item.TypeSituation || 0}`"
            label="Campaña"
            v-model="item.CmpID"
            ref="CmpIDRef"
            @input="onCampaignSelected"
            @datesLoaded="onCampaignDatesLoaded"
            :disabled="isFieldsDisabled"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-select-definition
            :def="1070"
            label="Situación especial"
            v-model="item.TypeSpecialSituation"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Situación Especial',
                required: true,
                cols: 12,
              },
            ]"
          />
        </v-col>
        <v-col
          v-if="
            item.TypeSpecialSituation == 4 || item.TypeSpecialSituation == 5
          "
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
        >
          <s-text
            label="Meses de Prueba"
            v-model="item.AgtMonthsProbation"
            type="number"
            :disabled="isFieldsDisabled"
          />
        </v-col>
      </v-row>

      <!-- Sección 10: Fechas del contrato -->
      <v-row>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            ref="admissionDateRef"
            v-model="item.AgtAdmissionDate"
            label="Fecha de Ingreso"
            :disabled="extension || renovation"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="4" lg="4">
          <s-date
            ref="beginDateRef"
            v-model="item.AgtBeginDate"
            label="Inicio de Contrato"
            :disabled="extension"
          />
        </v-col>
        <v-col
          v-if="!item._isIndeterminate"
          class="s-col-form"
          sm="4"
          md="4"
          lg="4"
        >
          <s-date
            :disabled="item._endDateLocked"
            v-model="item.AgtEndDate"
            label="Fin de Contrato"
            :min="item.AgtBeginDate"
          />
        </v-col>
      </v-row>

      <!-- Sección 11: Opciones adicionales y cese -->
      <v-row>
        <v-col class="s-col-form" xs="4" sm="2" md="4" lg="2">
          <s-switch label="Jornada Máxima" v-model="item.AgtMaximunDay" />
        </v-col>
        <v-col
          class="s-col-form"
          xs="4"
          sm="2"
          md="4"
          lg="2"
          v-show="item.AgtID > 0 && item.AgtStatus === 1"
        >
          <s-switch label="Registrar Cese" v-model="item.ceseActivo" />
        </v-col>
        <v-col class="s-col-form" sm="4" md="3" lg="4" v-if="item.ceseActivo">
          <s-select-definition
            ref="terminationRef"
            :def="1051"
            label="Motivo cese"
            :clearable="true"
            v-model="item.TypeTermination"
            :disabled="!item.ceseActivo"
            :add-custom="$fun.isAdmin()"
            :custom-fields="[
              {
                key: 'DedDescription',
                label: 'Nombre de Motivo de Cese',
                required: true,
                cols: 12,
              },
            ]"
          />
        </v-col>
        <v-col class="s-col-form" sm="4" md="3" lg="4" v-if="item.ceseActivo">
          <s-date
            v-model="item.AgtTerminationDate"
            label="Fecha cese"
            :disabled="!item.ceseActivo"
          />
        </v-col>
      </v-row>

      <!-- Sección 12: Detalle del servicio (Si aplica) -->
      <v-row v-if="item.TypeContract == 9">
        <v-col cols="12">
          <s-textarea
            label="Describa el Servicio"
            :height="80"
            v-model="item.AgtServiceDescription"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import SSelectArea from "../../../components/Utils/SSelectArea.vue";
import _sQuadriService from "@/services/HumanResource/hmnGang.js";
import _sProfService from "@/services/HumanResource/ProfessionsGen.js";
import SSelectBusinessLine from "../../../components/Utils/SSelectBusinessLine.vue";
import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import sToolbarPerson from "@/components/Utils/RecursosHm/sToolbarAgreementPerson";
import sEducationLevel from "@/services/HumanResource/HmnEducationalLevel.js";
import SSelectPosition from "../../../components/Utils/sSelectPosition.vue";
import _sWorkertService from "@/services/HumanResource/WorkerService.js";
import _constapp from "@/plugins/const";
import _sContractService from "@/services/HumanResource/HmnContractService.js";
import _sPositionService from "@/services/General/PositionService.js";
import SSelectDefinition from "../SSelectDefinition.vue";
import SProtectedAdminField from "@/components/Utils/RecursosHm/sProtectedAdminField.vue";
import SSelectEstablishment from "../../../components/Utils/SSelectEstablishment.vue";
import SSelectEducationalInstitution from "../../../components/Utils/SSelectEducationalInstitution.vue";
import SSelectEducationalMajor from "../../../components/Utils/SSelectEducationalMajor.vue";
import _sRecruitmentAccess from "@/services/HumanResource/RecruitmentAccessService";

export default {
  name: "sDataLaboral",
  components: {
    SSelectArea,
    SSelectBusinessLine,
    sToolbarPerson,
    SSelectPosition,
    SSelectDefinition,
    SProtectedAdminField,
    SSelectEstablishment,
    SSelectEducationalInstitution,
    SSelectEducationalMajor,
  },
  props: {
    visibleTitle: {
      type: Boolean,
      default: true,
    },
    full: {
      type: Boolean,
      default: false,
    },
    typeperson: {
      type: Number,
      default: 1,
    },
    addTypeSex: {
      type: Boolean,
      default: false,
    },
    addBirthDate: {
      type: Boolean,
      default: false,
    },
    addDriver: {
      type: Boolean,
      default: false,
    },
    datawork: { type: Object },
    extension: { type: Boolean, default: false },
    renovation: { type: Boolean, default: false },
  },
  data() {
    return {
      dataWor: {},
      profession: [],
      EducationLevels: [],
      quadrille: [],
      Position: [],
      editing: false,
      item: {
        TypePayroll: 1,
        AreID: null,
        AreName: null,
        CceName: null,
        PstID: null,
        CmpID: null,
        BslID: null,
        TypeWorker: 4,
        TypeWorkerName: "",
        PrnID: null,
        GngID: null,
        TypeSituation: 2,
        //fecha de hoy
        AgtAdmissionDate: null,
        AgtBeginDate: null,
        AgtEndDate: null,
        LveID: 7,
        TypeSpecialSituation: 1,
        TypeTermination: null,
        TypeCivilStatus: 1,
        AgtTerminationDate: null,
        TypeLaborRegime: 1,
        LaborRegimeText: "",
        ceseActivo: false,
        centercost: 0,
        typeCategory: 1,
        TypeContract: 11,
        TypeTurno: 1,
        TypeOccupationalCategory: 4,
        AgtSalary: 0,
        AgtChildrenQuantity: 0,
        AgtStatus: 1,
        ReqID: null,
        PrsDocumentNumber: null,
        AgtMonthsProbation: 3,
        AgtServiceDescription: null,
        PstIsBascCritic: 0,
        PstIsBascCriticName: "",
        _endDateLocked: false,
        _typeContractLocked: false,
        _reqEndDate: null,
        _cmpEndDate: null,
        EstID: null,
        TypeFormatLabor: null,
        EdiCode: null,
        EdmCode: null,
        PmaYearAdmission: null,
        PmaYearGraduation: null,
      },
      sourceLabel: null,
      sourceRequirements: [],
      assignedRequirementIds: [],
      vacancyStatus: {
        TotalVacancies: 0,
        CoveredVacancies: 0,
        ActiveContracts: 0,
      },
      selectedSourceRequirementKey: null,
      selectedCenterCost: 0,
      selectedBusinessLine: null,
      menu3: false,
      config: {},
      dialog: false,
      workert: null,
      lastLoadedPersonKey: null,
      sourceHydratedDocumentNumber: null,
      isSyncingContractDates: false,
      isResolvingWorker: false,
      isLoadingRequirementData: false,
    };
  },

  inject: ["contractDates"], // Recibe las fechas del padre
  computed: {
    hasProjections() {
      return !!this.vacancyStatus?.ProjectionsJSON;
    },
    parsedProjections() {
      if (!this.vacancyStatus?.ProjectionsJSON) return [];
      try {
        return JSON.parse(this.vacancyStatus.ProjectionsJSON);
      } catch (e) {
        return [];
      }
    },
    isProfessionalSource() {
      return this.sourceLabel === "Profesional";
    },
    hasSourceRequirements() {
      return this.sourceRequirementItems.length > 0;
    },
    sourceRequirementItems() {
      let filtered = this.sourceRequirements || [];
      if (!this.$fun.isAdmin() && !this.$fun.specialpermission) {
        const currentUserId = this.$fun.getUserID();
        filtered = filtered.filter((req) => {
          if (!req.AssignedUserIDs) return true;
          const userIds = req.AssignedUserIDs.split(',').map(Number);
          return userIds.includes(currentUserId);
        });
      }
      return filtered.map((requirement) => ({
        text: this.buildSourceRequirementLabel(requirement),
        value: this.buildSourceRequirementKey(requirement),
      }));
    },
    shouldLockPosition() {
      return false;
    },
    isFieldsDisabled() {
      return (
        this.extension || this.renovation || !!this.item._typeContractLocked
      );
    },
    isPrimaryFieldDisabled() {
      if (this.$fun.isAdmin()) {
        return false;
      }
      if (this.sourceLabel === "Anterior" || this.sourceLabel === "Personal" || this.sourceLabel === "Contrato anterior") {
        return false;
      }
      return !!this.item.ReqID || this.isFieldsDisabled;
    },
  },
  methods: {
    logTrace(step, payload = null) {
      // console.log silenciosos en producción para mejorar la performance
    },
    getFieldValue(source, fieldName) {
      if (!source) {
        return null;
      }

      if (source[fieldName] !== undefined) {
        return source[fieldName];
      }

      const camelCaseField =
        fieldName.charAt(0).toLowerCase() + fieldName.slice(1);

      if (source[camelCaseField] !== undefined) {
        return source[camelCaseField];
      }

      return null;
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    refreshHydratedSelectors() {
      this.$nextTick(() => {
        const areaLoad = this.$refs.areaRef?.$refs?.sarea?.load;
        const centerLoad = this.$refs.centerCostRef?.$refs?.sarea?.load;
        const positionLoad = this.$refs.positionRef?.$refs?.generic?.load;

        if (typeof areaLoad === "function") {
          areaLoad.call(this.$refs.areaRef.$refs.sarea);
        }

        if (typeof centerLoad === "function") {
          centerLoad.call(this.$refs.centerCostRef.$refs.sarea);
        }

        if (typeof positionLoad === "function") {
          positionLoad.call(this.$refs.positionRef.$refs.generic);
        }

        this.logTrace("refreshHydratedSelectors", {
          AreID: this.item.AreID,
          AreName: this.item.AreName,
          CceID: this.selectedCenterCost,
          CceName: this.item.CceName,
          PstID: this.item.PstID,
          PstName: this.item.PstName,
          CmpID: this.item.CmpID,
        });
      });
    },
    buildSourceRequirementKey(requirement) {
      if (!requirement) {
        return null;
      }

      return [
        requirement.ReqID || 0,
        requirement.PstID || 0,
        requirement.CceID || 0,
        requirement.CmpID || 0,
      ].join(":");
    },
    buildSourceRequirementLabel(requirement) {
      const parts = [];

      if (this.hasValue(requirement?.ReqID)) {
        parts.push(`REQ ${requirement.ReqID}`);
      }
      if (this.hasValue(requirement?.PstName)) {
        parts.push(requirement.PstName);
      }
      if (this.hasValue(requirement?.AreName)) {
        parts.push(requirement.AreName);
      }
      if (this.hasValue(requirement?.CceName)) {
        parts.push(requirement.CceName);
      }

      return parts.join(" | ");
    },
    normalizeContractSourceRequirements(contractSource) {
      const positionsRaw =
        this.getFieldValue(contractSource, "Positions") ||
        this.getFieldValue(contractSource, "positions") ||
        [];

      const positions = Array.isArray(positionsRaw)
        ? positionsRaw
            .map((position) => ({
              ReqID: this.getFieldValue(position, "ReqID"),
              AreID: this.getFieldValue(position, "AreID"),
              AreName: this.getFieldValue(position, "AreName"),
              CceID: this.getFieldValue(position, "CceID"),
              CceName: this.getFieldValue(position, "CceName"),
              PstID: this.getFieldValue(position, "PstID"),
              PstName: this.getFieldValue(position, "PstName"),
              CmpID: this.getFieldValue(position, "CmpID"),
              ReqEstBudget: this.getFieldValue(position, "ReqEstBudget"),
              CprStartDate: this.getFieldValue(position, "CprStartDate"),
              ReqEndContract: this.getFieldValue(position, "ReqEndContract"),
              TypePayroll: this.getFieldValue(position, "TypePayroll"),
              TypeLaborRegime: this.getFieldValue(position, "TypeLaborRegime"),
              TypeWorker: this.getFieldValue(position, "TypeWorker"),
              TypeContract: this.getFieldValue(position, "TypeContract"),
              CmpEndDate: this.getFieldValue(position, "CmpEndDate"),
            }))
            .filter(
              (position) =>
                this.hasValue(position?.ReqID) ||
                this.hasValue(position?.PstID) ||
                this.hasValue(position?.AreID) ||
                this.hasValue(position?.CceID),
            )
        : [];

      this.logTrace("normalizeContractSourceRequirements.positions", positions);

      if (positions.length > 0) {
        return positions;
      }

      const singleRequirement = {
        ReqID: this.getFieldValue(contractSource, "ReqID"),
        AreID: this.getFieldValue(contractSource, "AreID"),
        AreName: this.getFieldValue(contractSource, "AreName"),
        CceID: this.getFieldValue(contractSource, "CceID"),
        CceName: this.getFieldValue(contractSource, "CceName"),
        PstID: this.getFieldValue(contractSource, "PstID"),
        PstName: this.getFieldValue(contractSource, "PstName"),
        CmpID: this.getFieldValue(contractSource, "CmpID"),
        ReqEstBudget: this.getFieldValue(contractSource, "ReqEstBudget"),
        CprStartDate: this.getFieldValue(contractSource, "CprStartDate"),
        ReqEndContract: this.getFieldValue(contractSource, "ReqEndContract"),
        TypePayroll: this.getFieldValue(contractSource, "TypePayroll"),
        TypeLaborRegime: this.getFieldValue(contractSource, "TypeLaborRegime"),
        TypeWorker: this.getFieldValue(contractSource, "TypeWorker"),
        TypeContract: this.getFieldValue(contractSource, "TypeContract"),
        CmpEndDate: this.getFieldValue(contractSource, "CmpEndDate"),
      };

      this.logTrace(
        "normalizeContractSourceRequirements.singleRequirement",
        singleRequirement,
      );

      if (
        !this.hasValue(singleRequirement.ReqID) &&
        !this.hasValue(singleRequirement.PstID) &&
        !this.hasValue(singleRequirement.AreID) &&
        !this.hasValue(singleRequirement.CceID)
      ) {
        return [];
      }

      return [singleRequirement];
    },
    contractSourceHasVigenteRequirement(contractSource) {
      if (!contractSource) {
        return false;
      }

      const requirements =
        this.normalizeContractSourceRequirements(contractSource);
      if (requirements.length > 0) {
        return true;
      }

      const hasRequirementData = this.getFieldValue(
        contractSource,
        "HasRequirementData",
      );
      if (Number(hasRequirementData) === 1) {
        return true;
      }

      return this.hasValue(this.getFieldValue(contractSource, "ReqID"));
    },
    applyLastContractAsBase(agreement) {
      if (!agreement) {
        this.logTrace("applyLastContractAsBase.skip.noAgreement");
        return;
      }

      this.logTrace("applyLastContractAsBase.input", agreement);

      this.item = {
        ...this.item,
        AreID: agreement.AreID ?? this.item.AreID,
        AreName: agreement.AreName ?? this.item.AreName,
        PstID: agreement.PstID ?? this.item.PstID,
        PstName: agreement.PstName ?? this.item.PstName,
        TypeLaborRegime: agreement.TypeLaborRegime ?? this.item.TypeLaborRegime,
        LaborRegimeText: agreement.LaborRegimeText ?? this.item.LaborRegimeText,
        TypeContract: agreement.TypeContract ?? this.item.TypeContract,
        AgtSalary: this.$fun.isAdmin()
          ? this.$fun.decryptSalary(agreement.AgtSalary)
          : agreement.AgtSalary ?? this.item.AgtSalary,
        ReqID: null,
        CceID: null,
        CceName: null,
        CmpID: null,
      };

      this.selectedCenterCost = 0;
      this.selectedBusinessLine = null;
      this.selectedSourceRequirementKey = null;
      this.sourceRequirements = [];
      this.sourceLabel = "Contrato anterior";

      this.refreshHydratedSelectors();

      this.logTrace("applyLastContractAsBase.output", {
        AreID: this.item.AreID,
        PstID: this.item.PstID,
        TypeLaborRegime: this.item.TypeLaborRegime,
        TypeContract: this.item.TypeContract,
        AgtSalary: this.item.AgtSalary,
        ReqID: this.item.ReqID,
        CceID: this.selectedCenterCost,
        CmpID: this.item.CmpID,
      });
    },
    findSourceRequirementByKey(requirementKey) {
      return this.sourceRequirements.find(
        (requirement) =>
          this.buildSourceRequirementKey(requirement) === requirementKey,
      );
    },
    applyRequirementData(requirement) {
      if (!requirement) {
        return;
      }

      this.isLoadingRequirementData = true;

      this.logTrace("applyRequirementData.input", requirement);

      // Guardar fechas de referencia para la regla DedHelper2 de TypeContract
      this.item._reqEndDate = requirement.ReqEndContract || null;
      this.item._cmpEndDate = requirement.CmpEndDate || null;

      this.item.ReqID = this.hasValue(requirement.ReqID)
        ? requirement.ReqID
        : null;

      if (this.hasValue(requirement.AreID)) {
        this.item.AreID = requirement.AreID;
      }
      if (this.hasValue(requirement.AreName)) {
        console.log("Asignando AreName:", requirement.AreName);

        this.item.AreName = requirement.AreName;
      }
      if (this.hasValue(requirement.CceID)) {
        this.selectedCenterCost = requirement.CceID;
        this.item.CceID = requirement.CceID;
      }
      if (this.hasValue(requirement.CceName)) {
        this.item.CceName = requirement.CceName;
      }
      if (this.hasValue(requirement.CmpID)) {
        this.item.CmpID = requirement.CmpID;
        this.item.TypeSituation = 2;
      }
      if (this.hasValue(requirement.CprStartDate)) {
        this.setContractStartDatesToToday();
      }

      // Nuevos campos del requerimiento
      if (this.hasValue(requirement.TypePayroll)) {
        this.item.TypePayroll = requirement.TypePayroll;
      }
      if (this.hasValue(requirement.TypeLaborRegime)) {
        this.item.TypeLaborRegime = requirement.TypeLaborRegime;
      }
      if (this.hasValue(requirement.TypeWorker)) {
        this.item.TypeWorker = requirement.TypeWorker;
      }
      if (this.hasValue(requirement.TypeSituation)) {
        this.item.TypeSituation = requirement.TypeSituation;
      }

      // TypeContract: asignar valor numérico y marcar combo como bloqueado
      if (this.hasValue(requirement.TypeContract)) {
        this.$set(this.item, "_typeContractLocked", true);
        this.item.TypeContract = requirement.TypeContract;
      } else {
        this.$set(this.item, "_typeContractLocked", false);
      }

      // Aplicar reglas de fecha fin
      this.$nextTick(() => {
        this.updateContractEndDate();
      });

      let salaryResolved = 0;
      let hasValidSalary = false;

      if (requirement.ReqEstBudget) {
        let budget = null;
        try {
          budget = this.$fun.decrypt(requirement.ReqEstBudget);
        } catch (e) {
          budget = null;
        }
        
        if (budget && !isNaN(budget) && Number(budget) > 0) {
          salaryResolved = budget;
          hasValidSalary = true;
        } else if (requirement.ReqEstBudget && !isNaN(requirement.ReqEstBudget) && Number(requirement.ReqEstBudget) > 0) {
          salaryResolved = requirement.ReqEstBudget;
          hasValidSalary = true;
        } else if (requirement.ReqEstBudget === "FALTANTE") {
          salaryResolved = "FALTANTE";
          hasValidSalary = true;
        } else if (requirement.ReqEstBudget && typeof requirement.ReqEstBudget === "string" && requirement.ReqEstBudget.trim().length > 0) {
          salaryResolved = requirement.ReqEstBudget.trim();
          hasValidSalary = true;
        }
      }

      if (hasValidSalary) {
        this.item.AgtSalary = salaryResolved;
      } else {
        this.item.AgtSalary = 0;
        this.$fun.alert("El requerimiento seleccionado no cuenta con un sueldo de monto fijo establecido. Se asignará 0.00 temporalmente.", "warning");
      }

      const requirementPstID = requirement.PstID;
      const requirementPstName = requirement.PstName;

      this.$nextTick(() => {
        if (this.hasValue(requirementPstID)) {
          this.item.PstID = requirementPstID;
        }
        if (this.hasValue(requirementPstName)) {
          this.item.PstName = requirementPstName;
        }

        this.refreshHydratedSelectors();

        this.logTrace("applyRequirementData.output", {
          ReqID: this.item.ReqID,
          AreID: this.item.AreID,
          AreName: this.item.AreName,
          CceID: this.selectedCenterCost,
          CceName: this.item.CceName,
          CmpID: this.item.CmpID,
          PstID: this.item.PstID,
          PstName: this.item.PstName,
          AgtEndDate: this.item.AgtEndDate,
          TypePayroll: this.item.TypePayroll,
          TypeLaborRegime: this.item.TypeLaborRegime,
          TypeWorker: this.item.TypeWorker,
          TypeContract: this.item.TypeContract,
          TypeSituation: this.item.TypeSituation,
        });

        setTimeout(() => {
          this.isLoadingRequirementData = false;
        }, 300);
      });
    },
    handleSourceRequirementSelection(requirementKey) {
      this.logTrace("handleSourceRequirementSelection.key", requirementKey);
      const requirement = this.findSourceRequirementByKey(requirementKey);
      if (!requirement) {
        this.logTrace("handleSourceRequirementSelection.notFound", {
          requirementKey,
          sourceRequirements: this.sourceRequirements,
        });
        return;
      }

      this.applyRequirementData(requirement);
    },
    updateContractEndDate() {
      const dedID = this.item.TypeContract;
      if (dedID === null || dedID === undefined) {
        this.$set(this.item, "_endDateLocked", false);
        return;
      }

      // Buscar el item correspondiente en las definiciones del combo
      const selectComponent = this.$refs.typeContractRef;
      let items = [];
      if (selectComponent) {
        const genericComponent = selectComponent.$refs.sgeneric;
        if (genericComponent && genericComponent.items) {
          items = genericComponent.items;
        }
      }

      const obj = items.find((x) => x.DedID === dedID || x.DedValue === dedID);
      const rule = obj ? Number(obj.DedHelper2 ?? -1) : -1;

      const refDate = this.item._reqEndDate || this.item._cmpEndDate || null;

      if (rule === 0) {
        // Temporada / Bloqueada: siempre toma la fecha de fin de requerimiento/campaña
        this.item.AgtEndDate = refDate;
        this.$set(this.item, "_endDateLocked", true);
        this.$set(this.item, "_isIndeterminate", false);
      } else if (rule === 1) {
        // Pre-cargada pero editable
        this.item.AgtEndDate = refDate;
        this.$set(this.item, "_endDateLocked", false);
        this.$set(this.item, "_isIndeterminate", false);
      } else if (rule === 2) {
        // Contrato indeterminado: sin fecha fin
        this.item.AgtEndDate = null;
        this.$set(this.item, "_endDateLocked", true);
        this.$set(this.item, "_isIndeterminate", true);
      } else {
        // Sin regla de definición definida aún
        this.$set(this.item, "_endDateLocked", false);
        this.$set(this.item, "_isIndeterminate", false);
      }
    },
    onTypeContractChanged(dedID) {
      if (dedID === null || dedID === undefined) return;
      this.item.TypeContract = dedID;
      this.updateContractEndDate();
    },
    buildPersonKey(person) {
      if (!person) {
        return null;
      }

      const ntpID = person.NtpID || 0;
      const prsDocumentNumber =
        person.PrsDocumentNumber || person.GenPerson?.PrsDocumentNumber || "";

      return `${ntpID}:${prsDocumentNumber}`;
    },
    async hydratePersonContext(person) {
      if (!person) {
        this.lastLoadedPersonKey = null;
        this.logTrace("hydratePersonContext.skip.noPerson");
        return;
      }

      const personKey = this.buildPersonKey(person);
      if (!personKey || this.lastLoadedPersonKey === personKey) {
        this.logTrace("hydratePersonContext.skip.duplicated", {
          personKey,
          lastLoadedPersonKey: this.lastLoadedPersonKey,
        });
        return;
      }

      this.logTrace("hydratePersonContext.start", {
        personKey,
        person,
      });

      this.lastLoadedPersonKey = personKey;

      const prsDocumentNumber =
        person.PrsDocumentNumber || person.GenPerson?.PrsDocumentNumber || null;
      const ntpFullName =
        person.NtpFullName ||
        `${person.NtpName || ""} ${person.NtpPaternalSurname || ""} ${
          person.NtpMaternalSurname || ""
        }`.trim();

      this.item = {
        ...this.item,
        ...person,
        PrsDocumentNumber: prsDocumentNumber,
        NtpFullName: ntpFullName,
        NtpID: person.NtpID || null,
      };

      const wkrID = person.WkrID || person.GenPerson?.WkrID || null;
      let workerSearchResult = { hasActiveContract: false };
      if (person.NtpID && wkrID && wkrID > 0) {
        workerSearchResult = await this.searchWorkertByNtpID(person.NtpID);
      }

      if (!workerSearchResult.hasActiveContract && prsDocumentNumber) {
        const hasWorkerHistory = !!workerSearchResult?.workert?.WkrID;

        if (hasWorkerHistory) {
          const source = await this.fetchContractSourceByDocument(
            prsDocumentNumber,
          );
          const hasVigenteRequirement =
            this.contractSourceHasVigenteRequirement(source);

          if (hasVigenteRequirement) {
            this.logTrace("hydratePersonContext.useContractSource", {
              prsDocumentNumber,
            });
            this.applyContractSource(source);
          } else {
            this.logTrace("hydratePersonContext.useLastContractBase", {
              prsDocumentNumber,
            });
            this.applyLastContractAsBase(
              workerSearchResult?.lastAgreement || null,
            );
          }
        } else if (this.sourceHydratedDocumentNumber !== prsDocumentNumber) {
          await this.loadContractSourceByDocument(prsDocumentNumber);
        } else {
          this.logTrace("hydratePersonContext.skip.sourceAlreadyHydrated", {
            prsDocumentNumber,
          });
        }
      }

      this.logTrace("hydratePersonContext.end", {
        workerSearchResult,
        sourceLabel: this.sourceLabel,
        ReqID: this.item.ReqID,
        AreID: this.item.AreID,
        CceID: this.selectedCenterCost,
        CmpID: this.item.CmpID,
        PstID: this.item.PstID,
      });
    },
    initialize() {
      this.getProfession();
      this.getQuadrille();
      this.getEducationLevels();
      //this.getPositiom();
      //console.log("dataWork",this.datawork);
      this.TypeEducationLevel = 1;
    },

    // encryptSalary(salary){
    //   if (isNaN(salary)) {
    //     throw new Error("El salario debe ser un número válido");
    //   }
    //   const secretKey =_constapp._cKey;
    //   const encryptedSalary = CryptoJS.AES.encrypt(salary.toString(), secretKey).toString();
    //   return encryptedSalary;
    // },

    updateDates() {
      //console.log("Cambio de Fechas",this.contractDates);
      this.contractDates.AgtAdmissionDate = this.item.AgtAdmissionDate;
      this.contractDates.AgtEndDate = this.item.AgtEndDate;
    },
    getTodayDate() {
      return new Date().toISOString().substr(0, 10);
    },
    setContractStartDatesToToday() {
      const today = this.getTodayDate();

      this.item.AgtAdmissionDate = today;
      this.item.AgtBeginDate = today;
    },
    syncContractDates(sourceField) {
      if (this.isSyncingContractDates) {
        return;
      }

      const sourceValue = this.item[sourceField] || null;
      const targetField =
        sourceField === "AgtAdmissionDate"
          ? "AgtBeginDate"
          : "AgtAdmissionDate";

      if (this.item[targetField] === sourceValue) {
        return;
      }

      this.isSyncingContractDates = true;
      this.item[targetField] = sourceValue;

      this.$nextTick(() => {
        this.isSyncingContractDates = false;
        this.updateDates();
      });
    },
    onCampaignSelected(cmpID) {
      if (!cmpID) {
        this.setContractStartDatesToToday();
        this.item.AgtEndDate = null;
        this.item._cmpEndDate = null;
        this.updateDates();
      }
    },
    onCampaignDatesLoaded(dates) {
      const campaignEndDate = dates?.CmpEndDate || null;
      this.item._cmpEndDate = campaignEndDate;

      this.setContractStartDatesToToday();
      this.item.AgtEndDate = campaignEndDate;

      this.updateDates();
    },
    getDateBeginAndEnd() {
      let obj = {
        AgtAdmissionDate: this.item.AgtAdmissionDate,
        AgtEndDate: this.item.AgtEndDate,
      };
      return obj;
    },
    validateData() {
      //console.log("Data Laboral",this.item);

      if (!this.$fun.isAdmin() && !this.$fun.specialpermission) {
        if (this.assignedRequirementIds.length === 0) {
          this.$fun.alert(
            "Usted no tiene requerimientos asignados. Registro de contratos bloqueado.",
            "warning",
          );
          return false;
        }
        if (!this.item.ReqID) {
          this.$fun.alert(
            "Debe seleccionar un requerimiento asignado para registrar el contrato.",
            "warning",
          );
          return false;
        }
      }

      if (this.item.NtpID == null) {
        this.$fun.alert("Por favor seleccionar una Persona", "warning");
        return false;
      }

      if (this.hasSourceRequirements && !this.selectedSourceRequirementKey) {
        this.$fun.alert(
          "Debe seleccionar el requerimiento de origen para continuar con el contrato",
          "warning",
        );
        return false;
      }

      if (this.item.TypePayroll == null) {
        this.$fun.focusFieldError(
          this,
          "payrollRef",
          "El campo Planilla es obligatorio",
        );
        return false;
      }

      if (this.item.AreID == null) {
        this.$fun.focusFieldError(
          this,
          "areaRef",
          "El campo Area es obligatorio",
        );
        return false;
      }

      if (this.selectedCenterCost == null) {
        this.$fun.focusFieldError(
          this,
          "centerCostRef",
          "El campo Centro de Costo es obligatorio",
        );
        return false;
      }

      if (this.selectedBusinessLine == null) {
        this.$fun.focusFieldError(
          this,
          "businessLineRef",
          "El campo Linea de Negocio es obligatorio",
        );
        return false;
      }

      if (this.item.PstID == null) {
        this.$fun.focusFieldError(
          this,
          "positionRef",
          "El campo Cargo es obligatorio",
        );
        return false;
      }

      if (this.item.EstID == null) {
        this.$fun.alert(
          "El campo Establecimiento/Planta es obligatorio",
          "warning",
        );
        return false;
      }

      if (this.item.typeCategory == 4 && this.item.TypeFormatLabor == null) {
        this.$fun.alert(
          "El campo Modalidad Formativa es obligatorio para personal en formación",
          "warning",
        );
        return false;
      }

      if (this.item.AgtAdmissionDate == null) {
        this.$fun.focusFieldError(
          this,
          "admissionDateRef",
          "Fecha de Ingreso es obligatoria",
        );
        return false;
      }

      if (this.item.AgtBeginDate == null) {
        this.$fun.focusFieldError(
          this,
          "beginDateRef",
          "Inicio de Contrato es obligatorio",
        );
        return false;
      }

      if (this.item.AgtBeginDate && this.item.AgtEndDate) {
        const begin = new Date(this.item.AgtBeginDate);
        const end = new Date(this.item.AgtEndDate);
        if (!isNaN(begin.getTime()) && !isNaN(end.getTime())) {
          if (end <= begin) {
            this.$fun.alert(
              "La fecha de fin de contrato debe ser mayor a la fecha de inicio.",
              "warning",
            );
            return false;
          }
        }
      }

      if (this.item.ceseActivo) {
        if (this.item.TypeTermination == null) {
          this.$fun.focusFieldError(
            this,
            "terminationRef",
            "Motivo de Cese es obligatorio",
          );
          return false;
        }
      }

      // Validación de contrato de temporada (máximo 10 meses seguidos)
      const selectComponent = this.$refs.typeContractRef;
      let rule = -1;
      if (selectComponent) {
        const genericComponent = selectComponent.$refs.sgeneric;
        if (genericComponent && genericComponent.items) {
          const obj = genericComponent.items.find(
            (x) =>
              x.DedID === this.item.TypeContract ||
              x.DedValue === this.item.TypeContract,
          );
          rule = obj ? Number(obj.DedHelper2 ?? -1) : -1;
        }
      }

      if (rule === 0) {
        if (this.item.AgtAdmissionDate && this.item.AgtEndDate) {
          const admission = new Date(this.item.AgtAdmissionDate);
          const end = new Date(this.item.AgtEndDate);
          if (!isNaN(admission.getTime()) && !isNaN(end.getTime())) {
            const diffTime = end - admission;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays > 304) {
              this.$fun.alert(
                "El contrato de temporada no puede superar los 10 meses continuos (304 días) desde la fecha de ingreso.",
                "warning",
              );
              return false;
            }
          }
        }
      }

      return true;
    },

    applyContractSource(contractSource) {
      if (!contractSource) {
        return;
      }

      this.logTrace("applyContractSource.input", contractSource);

      this.sourceHydratedDocumentNumber =
        this.getFieldValue(contractSource, "PrsDocumentNumber") || null;

      this.sourceLabel =
        this.getFieldValue(contractSource, "SourceLabel") || null;
      this.item.PrsDocumentNumber =
        this.getFieldValue(contractSource, "PrsDocumentNumber") ||
        this.item.PrsDocumentNumber;
      this.item.NtpFullName =
        this.getFieldValue(contractSource, "NtpFullName") ||
        this.item.NtpFullName;
      this.item.NtpID =
        this.getFieldValue(contractSource, "NtpID") || this.item.NtpID;

      if (this.hasValue(this.getFieldValue(contractSource, "CmpID"))) {
        this.item.CmpID = this.getFieldValue(contractSource, "CmpID");
        this.item.TypeSituation = 2;
      }

      if (
        this.hasValue(this.getFieldValue(contractSource, "AgtChildrenQuantity"))
      ) {
        this.item.AgtChildrenQuantity = Number(
          this.getFieldValue(contractSource, "AgtChildrenQuantity"),
        );
      }
      if (
        this.hasValue(this.getFieldValue(contractSource, "TypeCivilStatus"))
      ) {
        this.item.TypeCivilStatus = Number(
          this.getFieldValue(contractSource, "TypeCivilStatus"),
        );
      }

      this.sourceRequirements =
        this.normalizeContractSourceRequirements(contractSource);
      this.logTrace(
        "applyContractSource.sourceRequirements",
        this.sourceRequirements,
      );

      const defaultRequirement =
        this.findSourceRequirementByKey(
          this.buildSourceRequirementKey({
            ReqID: this.getFieldValue(contractSource, "ReqID"),
            PstID: this.getFieldValue(contractSource, "PstID"),
            CceID: this.getFieldValue(contractSource, "CceID"),
            CmpID: this.getFieldValue(contractSource, "CmpID"),
          }),
        ) || (this.sourceRequirements.length > 0 ? this.sourceRequirements[0] : null);

      this.logTrace(
        "applyContractSource.defaultRequirement",
        defaultRequirement,
      );

      if (defaultRequirement) {
        this.selectedSourceRequirementKey =
          this.buildSourceRequirementKey(defaultRequirement);
        this.applyRequirementData(defaultRequirement);
      } else {
        this.selectedSourceRequirementKey = null;
        this.item.ReqID = null;
      }

      if (
        !defaultRequirement &&
        this.isProfessionalSource &&
        this.hasValue(this.getFieldValue(contractSource, "PstID"))
      ) {
        this.item.PstID = this.getFieldValue(contractSource, "PstID");
      }

      if (
        !defaultRequirement &&
        this.hasValue(this.getFieldValue(contractSource, "PstName"))
      ) {
        this.item.PstName = this.getFieldValue(contractSource, "PstName");
      }

      if (
        !defaultRequirement &&
        this.getFieldValue(contractSource, "ReqEstBudget")
      ) {
        const budget = this.$fun.decrypt(
          this.getFieldValue(contractSource, "ReqEstBudget"),
        );
        this.item.AgtSalary = budget || this.item.AgtSalary;
      }

      if (
        !defaultRequirement &&
        this.hasValue(this.getFieldValue(contractSource, "CprStartDate"))
      ) {
        this.setContractStartDatesToToday();
      }

      if (
        !defaultRequirement &&
        this.hasValue(this.getFieldValue(contractSource, "ReqEndContract"))
      ) {
        this.item.AgtEndDate = this.getFieldValue(
          contractSource,
          "ReqEndContract",
        );
      }

      this.logTrace("applyContractSource.output", {
        sourceLabel: this.sourceLabel,
        ReqID: this.item.ReqID,
        AreID: this.item.AreID,
        AreName: this.item.AreName,
        CceID: this.selectedCenterCost,
        CceName: this.item.CceName,
        CmpID: this.item.CmpID,
        PstID: this.item.PstID,
        PstName: this.item.PstName,
        AgtEndDate: this.item.AgtEndDate,
        sourceRequirements: this.sourceRequirements,
      });
    },

    resetContractSourceState() {
      this.sourceLabel = null;
      this.sourceRequirements = [];
      this.selectedSourceRequirementKey = null;
      this.sourceHydratedDocumentNumber = null;
      this.item.ReqID = null;
    },

    async fetchContractSourceByDocument(prsDocumentNumber) {
      if (!prsDocumentNumber) {
        return null;
      }

      const response = await _sContractService.contractSourceByDocument(
        prsDocumentNumber,
      );
      return response?.data || null;
    },

    async loadContractSourceByDocument(prsDocumentNumber) {
      if (!prsDocumentNumber) {
        this.resetContractSourceState();
        this.logTrace("loadContractSourceByDocument.skip.noDocument");
        return;
      }

      try {
        this.logTrace("loadContractSourceByDocument.request", {
          prsDocumentNumber,
        });
        const source = await this.fetchContractSourceByDocument(
          prsDocumentNumber,
        );
        if (!source) {
          this.resetContractSourceState();
          this.logTrace("loadContractSourceByDocument.emptyResponse", {
            prsDocumentNumber,
          });
          return;
        }

        this.logTrace("loadContractSourceByDocument.response", source);

        this.applyContractSource(source);
      } catch (error) {
        this.resetContractSourceState();
        this.logTrace("loadContractSourceByDocument.error", error);
        const message =
          error?.response?.data?.Message ||
          error?.response?.data ||
          "No se pudo obtener el origen del contrato";
        this.$fun.alert(message, "error");
      }
    },

    clear() {},

    close() {
      this.$refs.document.focus();
      this.$emit("toclose", 2);
      this.clear();
    },
    save() {},
    getProfession() {
      let obj = {
        prnStatus: 1,
      };
      _sProfService.list(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
          this.profession = resp.data;
        }
      });
    },

    getEducationLevels() {
      sEducationLevel.list(this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
          this.EducationLevels = resp.data;
          //this.TypeEducationLevel = 1;
        }
      });
    },
    getQuadrille() {
      let obj = {
        SecStatus: 1,
      };
      _sQuadriService.list(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
          this.quadrille = resp.data;
        }
      });
    },
    getPositiom() {
      let obj = { AreID: null, TypePosition: 1 };
      _sCttoService.Positionlist(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
          this.Position = resp.data;
        }
      });
    },
    applyActiveAgreementData(activeWorker) {
      const agreement = activeWorker?.AgreementCE || null;
      if (!agreement) {
        this.logTrace(
          "applyActiveAgreementData.skip.noAgreement",
          activeWorker,
        );
        return;
      }

      this.workert = activeWorker;

      this.item = {
        ...this.item,
        ...agreement,
        NtpID: this.item.NtpID,
        NtpFullName: this.item.NtpFullName,
        PrsDocumentNumber: this.item.PrsDocumentNumber,
        AgtSalary: this.$fun.isAdmin()
          ? this.$fun.decryptSalary(agreement.AgtSalary)
          : agreement.AgtSalary,
        ceseActivo: !!agreement.AgtTerminationDate,
      };

      this.selectedCenterCost = agreement.CceID || 0;
      this.selectedBusinessLine = agreement.BslID || null;

      this.logTrace("applyActiveAgreementData.output", {
        AgtID: agreement.AgtID,
        WkrID: activeWorker?.WkrID || null,
        ReqID: this.item.ReqID,
        AreID: this.item.AreID,
        CceID: this.selectedCenterCost,
        BslID: this.selectedBusinessLine,
        PstID: this.item.PstID,
        CmpID: this.item.CmpID,
      });

      this.refreshHydratedSelectors();
    },
    getData() {
      if (this.validateData()) {
        let salaryEncrypted = isNaN(this.item.AgtSalary)
          ? this.item.AgtSalary
          : this.$fun.encryptSalary(String(this.item.AgtSalary || 0));

        let salaryNumber = 0;
        if (this.item.AgtSalary) {
          if (!isNaN(this.item.AgtSalary)) {
            salaryNumber = Number(this.item.AgtSalary);
          } else {
            try {
              const decrypted = this.$fun.decryptSalary(this.item.AgtSalary);
              salaryNumber = Number(decrypted);
            } catch (e) {
              salaryNumber = 0;
            }
          }
        }

        if (!Number.isNaN(salaryNumber) && salaryNumber <= 0) {
          this.$fun.alert(
            "Advertencia: el sueldo esta en 0. Un administrador debe revisarlo antes de firmar el contrato.",
            "warning",
          );
        }

        return {
          //AgtID : null , //this.AgtID,
          AgtChildrenQuantity: this.item.AgtChildrenQuantity, // Num Hijos
          AgtBeginDate: this.item.AgtBeginDate,
          AgtEndDate: this.item.AgtEndDate,
          AgtTerminationDate: this.item.ceseActivo
            ? this.item.AgtTerminationDate
            : null,
          AgtMaximunDay: this.item.AgtMaximunDay,
          AgtSalary: salaryEncrypted,
          AgtAdmissionDate:
            this.item.AgtAdmissionDate ??
            new Date().toISOString().substr(0, 10),
          AgtStatus: this.item.ceseActivo ? 2 : 0,
          WkrID: this.workert ? this.workert.WkrID : 0,
          TypePayroll: this.item.TypePayroll,
          AreID: this.item.AreID,
          CceID: this.selectedCenterCost,
          TypeCategory: this.item.typeCategory,
          CmpID: this.item.TypeSituation == 2 ? this.item.CmpID : null,
          GngID: this.item.GngID,
          BslID: this.selectedBusinessLine,
          TypeSituation: this.item.TypeSituation,
          TypeSpecialSituation: this.item.TypeSpecialSituation,
          TypeCivilStatus: this.item.TypeCivilStatus,
          TypeWorker: this.item.TypeWorker,
          TypeLaborRegime: this.item.TypeLaborRegime,
          TypeOccupationalCategory: this.item.TypeOccupationalCategory,
          LveID: null,
          PstID: this.item.PstID,
          PrnID: this.item.PrnID,
          TypeContract: this.item.TypeContract,
          TypeTermination: this.item.ceseActivo
            ? this.item.TypeTermination
            : null,
          TypeTurno: this.item.TypeTurno,
          AgtRenovation: 0, // Renovacion
          ReqID: this.item.ReqID || null,
          RqpID: this.item.ReqID || 0, // compatibilidad temporal
          AgtServiceDescription: this.item.AgtServiceDescription,
          AgtMonthsProbation: this.item.AgtMonthsProbation,
          // Nuevos campos
          EstID: this.item.EstID || null,
          TypeFormatLabor: this.item.TypeFormatLabor || null,
          EdiCode: null,
          EdmCode: null,
          PmaYearAdmission: null,
          PmaYearGraduation: null,
          //ceseActivo: this.item.ceseActivo,
          //AmsID:null,//Movimiento de Contrato
          SecStatus: 1,
          UsrCreateID: this.$fun.getUserID(),
          Supervision: this.item.TypeSpecialSituation == 3 ? 0 : 1,
          //Persona de Contrato
          NtpID: this.item.NtpID,
          workert: this.workert || null,
        };
      } else {
        return false;
      }
    },
    async returnPerson(value, item) {
      //console.log("Valueeeeee", value);
      if (value != null) {
        item.NtpFullName = value.NtpFullName;
        item.NtpID = value.NtpID;
        await this.hydratePersonContext({
          ...value,
          PrsDocumentNumber: item.PrsDocumentNumber,
        });
      } else {
        item.NtpFullName = null;
        item.NtpID = null;
        this.lastLoadedPersonKey = null;
        this.resetContractSourceState();
      }
    },

    async searchWorkertByNtpID(ntpID) {
      this.isResolvingWorker = true;
      try {
        this.logTrace("searchWorkertByNtpID.request", { ntpID });
        // Buscar trabajador por NtpID
        const response = await _sWorkertService.searchworkertByNtpID(
          ntpID,
          this.$fun.getUserID(),
        );
        this.workert = response.data;

        this.logTrace("searchWorkertByNtpID.workert", this.workert);

        if (!this.workert) {
          this.$emit("updateContractsPast", null);
          return { hasActiveContract: false, workert: null };
        } // Si no hay trabajador, detener la ejecución

        if (!this.workert.AgreementCE?.AgtID) {
          this.$emit("updateContractsPast", null);
          return { hasActiveContract: false, workert: this.workert };
        } //No hay contratos pasados

        const activeAgreementID = this.workert.ActiveAgtID || null;

        if (activeAgreementID) {
          const activeWorkerResponse = await _sContractService.workerPerson(
            this.workert.WkrID,
            activeAgreementID,
            this.$fun.getUserID(),
          );
          const activeWorker = activeWorkerResponse?.data || null;

          if (activeWorker?.AgreementCE) {
            this.logTrace(
              "searchWorkertByNtpID.activeContract.workerPerson",
              activeWorker,
            );
            this.applyActiveAgreementData(activeWorker);
            this.$emit("updateContractsPast", activeWorker);
          } else {
            this.$emit("updateContractsPast", null);
          }

          this.$fun.alert(
            "Este trabajador tiene un contrato activo",
            "warning",
          );
          console.log(
            "Contrato activo encontrado para el trabajador:",
            activeWorker?.AgreementCE || activeAgreementID,
          );

          return {
            hasActiveContract: true,
            workert: activeWorker || this.workert,
            contract: activeWorker?.AgreementCE || null,
          };
        }

        // Buscar contratos pasados
        const workerPersonResponse = await _sContractService.workerPerson(
          this.workert.WkrID,
          this.workert.AgreementCE?.AgtID || null,
          this.$fun.getUserID(),
        );

        const contractWokert = workerPersonResponse.data;

        if (workerPersonResponse.status === 200) {
          this.logTrace(
            "searchWorkertByNtpID.pastContracts",
            workerPersonResponse.data,
          );
        }

        if (contractWokert) {
          this.contractsPast = workerPersonResponse.data;

          // Emitir el evento a la vista padre (Contract)
          this.$emit("updateContractsPast", this.contractsPast);
        }

        return {
          hasActiveContract: false,
          workert: this.workert,
          lastAgreement:
            contractWokert?.AgreementCE || this.workert?.AgreementCE || null,
        };
      } catch (error) {
        console.error("Error en searchWorkertByNtpID:", error);
        const errorMessage =
          error.response?.data?.Message || "Ocurrió un error inesperado";
        this.$fun.alert(`Error: ${errorMessage}`, "error");
        return { hasActiveContract: false, workert: null };
      } finally {
        this.isResolvingWorker = false;
      }
    },
    fetchVacancyStatus(reqID) {
      _sContractService
        .requirementVacancyStatus(reqID, this.$fun.getUserID())
        .then((resp) => {
          if (resp.status === 200 && resp.data) {
            this.vacancyStatus = resp.data;
          }
        })
        .catch((error) => {
          console.error("Error al obtener estatus de vacantes:", error);
        });
    },
  },
  watch: {
    datawork: {
      async handler(newVal) {
        if (newVal) {
          this.logTrace("watch.datawork", newVal);
          await this.hydratePersonContext(newVal);
        }
      },
      immediate: true,
    },
    "item.CmpID"() {
      //console.log("itemsssss",this.$refs.CmpIDRef );
    },
    "item.ReqID": {
      handler(newVal) {
        if (newVal) {
          this.fetchVacancyStatus(newVal);
        } else {
          this.vacancyStatus = {
            TotalVacancies: 0,
            CoveredVacancies: 0,
            ActiveContracts: 0,
          };
        }
      },
      immediate: true,
    },
    selectedSourceRequirementKey(newValue) {
      this.logTrace("watch.selectedSourceRequirementKey", newValue);
      if (!newValue) {
        if (!this.isProfessionalSource) {
          this.item.ReqID = null;
        }
        return;
      }

      this.handleSourceRequirementSelection(newValue);
    },
    "item.PstID"() {
      if (this.isLoadingRequirementData) return;
      console.log("Cambio de Cargo", this.item.PstID);
      if (!this.item.PstID) {
        this.item.PstName = "";
        this.item.PstIsBascCritic = 0;
        this.item.PstIsBascCriticName = "";
        return;
      }

      _sPositionService
        .searchByPstID(this.item.PstID, this.$fun.getUserID())
        .then((x) => {
          let postion = x.data;
          console.log("cargo posicion = ", postion);
          this.item.TypeLaborRegime = postion.TypeLaborRegime;
          this.item.LaborRegimeText = postion.LaborRegimeText;
          this.item.TypeWorker = postion.TypePosition;
          this.item.TypeWorkerName = postion.TypePositionName;
          this.item.PstIsBascCritic = postion.PstIsBascCritic;
          this.item.PstIsBascCriticName = postion.PstIsBascCriticName;
        });
    },
    "item.AreID"(newVal) {
      if (this.isLoadingRequirementData) return;
      console.log("Cambio de Área", newVal);
      if (!newVal) {
        this.item.AreName = "";
      }
    },
    "item.AgtAdmissionDate"() {
      this.syncContractDates("AgtAdmissionDate");
      this.updateDates();
    },
    "item.AgtEndDate"() {
      this.updateDates();
    },
    "item.AgtBeginDate"() {
      this.syncContractDates("AgtBeginDate");
    },
  },
  created() {
    if (this.datawork && typeof this.datawork === "object") {
      this.dataWor = Object.assign({}, this.datawork);
    }
    this.setContractStartDatesToToday();
    this.initialize();
  },
  mounted() {
    //console.log("Items", this.$refs.CmpIDRef);
    //this.$refs.CmpIDRef
  },
};
</script>
<style scoped>
.transparent-table {
  background: transparent !important;
}
.transparent-table th, .transparent-table td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.border-t-dotted {
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
}
</style>
